/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoHowToSteps } from '@cloudio-saas/datasource-types';
import { emptyFunction } from '@cloudio-saas/statex';
import { Alert, Box, Typography } from '@mui/material';
import { openURL } from 'actions/ActionOpenURL/ActionOpenURL';
import SlateRTE from 'components/RichTextEditor/Slate';
import { getUrl } from 'config/config';
import { useTheme } from 'theme/Theme';
import { HowToEntry, useHowTos } from './HowToHome';
import StepContainer from './StepContainer';

export default function HowToStep({
  step,
  index,
}: {
  step: DBRow<IoHowToSteps>;
  index: number;
}) {
  if (step.stepTitle.startsWith('SUB_STEP:')) {
    return (
      <HowToSubStep
        appUid={step.appUid}
        howToUid={step.stepTitle.substring(9)}
        index={index}
        step={step}
      />
    );
  }
  return (
    <StepContainer
      index={index}
      title={<Typography variant="h6">{step.stepTitle ?? ''}</Typography>}
    >
      {step.stepDescription ? (
        <Box>
          <SlateRTE
            autoFocus={false}
            width="100%"
            height="auto"
            value={step.stepDescription}
            onChange={emptyFunction}
            readOnly
          />
        </Box>
      ) : null}
      {step.imageUid ? (
        <Box sx={{ mt: 2 }}>
          <img
            src={`${getUrl()}inline/public/${step.appUid}/${step.imageUid}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: '0.5em',
              overflow: 'hidden',
              boxShadow:
                '0 0 #0000,0 0 #0000,0 1px 3px 0 rgba(15,23,42,.1),0 1px 2px -1px rgba(15,23,42,.1)',
            }}
          />
        </Box>
      ) : null}
    </StepContainer>
  );
}

export function HowToSubStep({
  appUid,
  howToUid,
  index,
  step,
}: {
  appUid: string;
  howToUid: string;
  index: number;
  step: DBRow<IoHowToSteps>;
}) {
  const { howTos, loading } = useHowTos(appUid);
  const theme = useTheme();
  if (loading) return null;
  const howTo = howTos.find((h) => h.howToUid === howToUid);

  if (!howTo)
    return (
      <Alert severity="error">Sub step how to not found! {howToUid}</Alert>
    );

  return (
    <StepContainer
      index={index}
      title={
        <Typography variant="h6">
          Perform the steps from the below how to
        </Typography>
      }
    >
      {step.stepDescription ? (
        <Box>
          <SlateRTE
            autoFocus={false}
            width="100%"
            height="auto"
            value={step.stepDescription}
            onChange={emptyFunction}
            readOnly
          />
        </Box>
      ) : null}
      <Box sx={{ mt: 2 }}>
        <HowToEntry
          key={howToUid}
          appUid={appUid}
          howTo={howTo}
          index={index}
          theme={theme}
          sx={{
            m: 0,
            boxShadow: '1px 1px 3px 1px rgba(15,23,42,.1)',
          }}
          onClick={() => {
            openURL(`#/how-to/${appUid}/${howTo.howToUid}`, true);
          }}
        />
      </Box>
    </StepContainer>
  );
}
